import { useCallback, useMemo, useState } from "react";

import { MenuVerticalFilled } from "@aircall/icons";
import {
  Box,
  Button,
  Dropdown,
  Flex,
  Menu,
  MenuItem,
  Typography,
} from "@aircall/tractor";
import { useForm } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import { useDeleteEvaluation } from "@/hooks/useDeleteEvaluation/useDeleteEvaluation";
import { EvaluationAccordionHeaderContent } from "../EvaluationAccordionHeaderContent";

import { useUpdateEvaluation } from "../../hooks/useUpdateEvaluation/useUpdateEvaluation";
import {
  EvaluationUpdateFormBinaryQuestion,
  EvaluationUpdateFormRatingQuestion,
} from "./components/EvaluationFormQuestion";
import { EvaluationUpdateFormFooter } from "./components/EvaluationUpdateFormFooter";
import { EvaluationUpdateFormProvider } from "./contexts/EvaluationUpdateForm/EvaluationUpdateFormContext";

import { Accordion } from "@/components/Accordion";
import { AccordionBody } from "@/components/Accordion/components/AccordionBody";
import { AccordionHeader } from "@/components/Accordion/components/AccordionHeader";
import { AccordionItem } from "@/components/Accordion/components/AccordionItem";
import { ConfirmationModal } from "@/components/ConfirmationModal";
import { useBoolean } from "@/hooks/useBoolean/useBoolean";

import {
  AccordionItemId,
  EvaluationUpdateFormProps,
  UpdateEvaluationOverride,
  initRatingScales,
  initSet,
  isBinaryEvaluationQuestion,
  isRatingEvaluationQuestion,
  transformFormValueIntoInput,
} from ".";

export function EvaluationUpdateForm({
  evaluation,
  revieweeUserId,
}: EvaluationUpdateFormProps) {
  const navigate = useNavigate();

  const { t } = useTranslation();

  const { callId } = useParams();

  const { deleteEvaluation, loadingDeleteEvaluation } = useDeleteEvaluation();

  const { updateEvaluation, loadingUpdateEvaluation } = useUpdateEvaluation({
    onUpdateSuccess: () => {
      navigate("../");
    },
  });

  const {
    value: isConfirmationModalOpen,
    setFalse: closeConfirmationModal,
    setTrue: openConfirmationModal,
  } = useBoolean();

  // Map answer values to custom points.
  const ratingScales = useMemo(
    () => initRatingScales(evaluation),
    [evaluation]
  );

  const form = useForm<UpdateEvaluationOverride>({
    onSubmit: async ({ value }) => {
      updateEvaluation(transformFormValueIntoInput(value));
    },
    defaultValues: {
      categories: evaluation.categories,
      feedback: evaluation.feedback ?? "",
      // Below are hidden fields.
      revieweeUserId,
      ID: evaluation.ID,
    },
  });

  // Accordion state.
  const [openedItems, setOpenedItems] = useState<Set<AccordionItemId>>(
    () => new Set(initSet(form.state.values.categories))
  );

  const handleOnClickAccordionItem = useCallback((id: AccordionItemId) => {
    setOpenedItems((prevSet) => {
      const newSet = new Set(prevSet);

      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }

      return newSet;
    });
  }, []);

  const cancelHandler = () => {
    navigate("../");
  };

  const removeEvaluationHandler = async () => {
    if (callId) {
      await deleteEvaluation({ callID: callId });
    }
  };

  const handleOnClickChevron = useCallback(
    (categoryId: AccordionItemId) => () => {
      handleOnClickAccordionItem(categoryId);
    },
    [handleOnClickAccordionItem]
  );

  return (
    <>
      <EvaluationUpdateFormProvider form={form} ratingScales={ratingScales}>
        <Flex flexDirection="column" py="xs" gap="xxs">
          <Flex
            justifyContent="space-between"
            alignItems="center"
            p="xxs s"
            borderBottom="1px solid"
            borderColor="graphic-default"
            data-test="edit-evaluation"
          >
            <Typography variant="headingBoldS">
              {evaluation.scoreCardName}
            </Typography>
            <Dropdown
              trigger={
                <Button size="xSmall" variant="secondary" mode="ghost">
                  <MenuVerticalFilled />
                </Button>
              }
            >
              <Menu>
                <MenuItem onClick={openConfirmationModal}>
                  {t("evaluation.remove")}
                </MenuItem>
              </Menu>
            </Dropdown>
          </Flex>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
          >
            <Flex flexDirection="column" gap="m">
              <Box backgroundColor="surface-default">
                <Accordion openedItems={openedItems}>
                  {form.state.values.categories.map(
                    (category, categoryIndex) => (
                      <AccordionItem
                        key={category.ID}
                        id={category.ID}
                        borderBottom="1px solid"
                        borderColor="graphic-default"
                      >
                        <AccordionHeader
                          onClick={handleOnClickChevron(category.ID)}
                          cursor="pointer"
                        >
                          {/* We could put the remaining number of questions in the header */}
                          <EvaluationAccordionHeaderContent
                            title={category.categoryName}
                            open={openedItems.has(category.ID)}
                          />
                        </AccordionHeader>
                        <AccordionBody>
                          <Flex p="s" flexDirection="column" gap="xl">
                            {form.state.values.categories[
                              categoryIndex
                            ].questions.map((question, questionIndex) => {
                              if (isRatingEvaluationQuestion(question)) {
                                return (
                                  <EvaluationUpdateFormRatingQuestion
                                    key={`${category.categoryOrder}-${questionIndex}`}
                                    order={question.questionOrder + 1}
                                    text={question.questionText}
                                    possibleAnswers={
                                      question.questionPossibleAnswers
                                    }
                                    categoryIndex={categoryIndex}
                                    questionIndex={questionIndex}
                                  />
                                );
                              }

                              if (isBinaryEvaluationQuestion(question)) {
                                return (
                                  <EvaluationUpdateFormBinaryQuestion
                                    key={`${category.categoryOrder}-${questionIndex}`}
                                    order={question.questionOrder + 1}
                                    text={question.questionText}
                                    possibleAnswers={
                                      question.questionPossibleAnswers
                                    }
                                    categoryIndex={categoryIndex}
                                    questionIndex={questionIndex}
                                  />
                                );
                              }

                              throw new Error("Unknown question type.");
                            })}
                          </Flex>
                        </AccordionBody>
                      </AccordionItem>
                    )
                  )}
                </Accordion>
              </Box>
              <EvaluationUpdateFormFooter
                loading={loadingUpdateEvaluation}
                onClickCancel={cancelHandler}
              />
            </Flex>
          </form>
        </Flex>
      </EvaluationUpdateFormProvider>
      <ConfirmationModal
        onHide={closeConfirmationModal}
        show={isConfirmationModalOpen}
        loading={loadingDeleteEvaluation}
        actions={[
          {
            label: t("cancel"),
            onClick: closeConfirmationModal,
          },
          {
            label: t("evaluation.remove_it"),
            onClick: removeEvaluationHandler,
          },
        ]}
        title={t("evaluation.remove")}
        description={t("evaluation.cancel_evaluation_description", {
          scoreCardName: evaluation.scoreCardName,
        })}
      />
    </>
  );
}
